<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <div v-else>
    <EditableComponent
      v-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :active="componentEditMode && componentEditMode?.id === id"
      :duplicate="componentPermissions.canDuplicate"
      :edit="componentPermissions.canEdit"
      :excludeStylables="[ThemeComponentOptionEnum.COLOR_PICKER, ThemeComponentOptionEnum.PADDING]"
      :remove="componentPermissions.canRemove"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      stylable
      @duplicate="duplicate(id)"
      @edit="setComponentEditMode(ComponentTypes.IFrame, id)"
      @remove="removeComponentById(id)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
    >
      <IFrameEdit :data="data" />
    </EditableComponent>
    <IFrameRaw
      v-else
      :id="id"
      :data="data"
      :used-in-section-id="usedInSectionId"
      v-bind="$attrs"
      @iframe-wrapper-clicked="setComponentSelectedMode(id)"
    />
  </div>
</template>

<script lang="ts" setup>
import { toRefs, onMounted, type CSSProperties } from 'vue'

import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import type { IFrameProps, ComponentNode } from '@canvas-builder/types/canvas'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'
import IFrameRaw from './IFrame.raw.vue'
import IFrameEdit from './IFrame.edit.vue'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import type { CanvasRetrieve } from '@openapi'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: IFrameProps
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: CSSProperties
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: CSSProperties]
}>()

const { id, usedInSectionId, when_used_in_section } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const {
  mode,
  isEditMode,
  removeComponentById,
  setComponentEditMode,
  componentEditMode,
  setComponentSelectedMode,
  componentSelectedMode,
  justAddedComponentId,
  duplicate,
  isViewOnlyMode,
  setComponentSettingsMode,
} = useCanvas()

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    setComponentEditMode(ComponentTypes.IFrame, props.id)
  }
})
</script>
